import React, { memo, MouseEventHandler, useEffect, useRef, useState, useCallback } from 'react'
import ReactPlayer from 'react-player/lazy'
import clsx from 'clsx'
import { RootState } from '../../redux/store'
import rewindIcon from '../../assets/images/rewind-icon.svg'
import pauseIcon from '../../assets/images/pause-icon.svg'
import ccIcon from '../../assets/images/cc-icon.svg'
import rewindFullIcon from '../../assets/images/rewind-full-icon.svg'
import pauseFullIcon from '../../assets/images/pause-full-icon.svg'
import ccFullIcon from '../../assets/images/cc-full-icon.svg'
import playIcon from '../../assets/images/play-icon.svg'
import { useSelector } from 'react-redux'
import useSetFocusById from '../../hooks/useSetFocus'
import { Button } from '../atoms'
import ControlHighlight from '../molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../molecules/KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { hasUrlFeatureFlag, hasUrlFeatureFlagTrain } from '../molecules/URLFeatureFlag/URLFeatureFlag'
import { OnProgressProps } from 'react-player/base'
// PreferenceType import removed as unused
import { useUserPreferences } from '../../contexts/UserPreferencesContext'

interface VideoPlayerProps {
  handleEnded: (url?: string) => void
  handleProgress?: (state: OnProgressProps) => void
  handlePlay?: () => void
  handlePause?: () => void
  isPlaying?: boolean
  subTitlesUrl?: string
  url: string
  instructionMode: boolean
  muted?: boolean
  pauseOnFocusLost?: boolean
  secondaryLeftComponent?: React.ReactNode
  controlLeftComponent?: React.ReactNode
  controlRightComponent?: React.ReactNode
}

export enum FocusableElement {
  ButtonRewind = 'button-rewind',
  ButtonPlayPause = 'button-play-pause',
  ButtonCc = 'button-cc',
}

export const idVideoPlayer = 'react-player'
export const idControls = 'controls'

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  handleEnded,
  handleProgress,
  handlePlay = () => {},
  handlePause = () => {},
  isPlaying,
  subTitlesUrl,
  url,
  instructionMode,
  muted,
  pauseOnFocusLost = true,
  secondaryLeftComponent,
  controlLeftComponent,
  controlRightComponent
}) => {
  const playerRef = useRef<ReactPlayer | null>(null)
  const playerRefTimer = useRef<number | null>(null)
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false)
  const [playing, setPlaying] = useState<boolean>(false)
  const [hideControls, setHideControls] = useState<boolean>(false)
  const { playerButtonsDisabled } = useSelector((state: RootState) => state.player)
  const keyboardControls = useKeyboardControls()
  const userPreferences = useUserPreferences()
  const [setFocusById] = useSetFocusById()
  const urlFeatureFlagEnabled = hasUrlFeatureFlag()
  const urlFeatureFlagTrainEnabled = hasUrlFeatureFlagTrain()

  // Listen for captions changes from other components (like StudentMenu)
  useEffect(() => {
    const handleCaptionsChange = (e: Event) => {
      const enabled = (e as CustomEvent).detail
      userPreferences.setPreferenceA11yCc(Boolean(enabled))
    }
    
    window.addEventListener('captionsChanged', handleCaptionsChange as EventListener)
    return () => window.removeEventListener('captionsChanged', handleCaptionsChange as EventListener)
  }, [userPreferences])

  useEffect(() => {
    const playing = isPlaying !== undefined ? isPlaying : false
    setPlaying(playing)
    setHideControls(instructionMode)
  }, [isPlaying, instructionMode])

  useEffect(() => {
    setFocusById(FocusableElement.ButtonPlayPause)
  }, [setFocusById])

  useEffect(() => {
    const pauseVideoFocusLost = (): void => {
      // Using the feature flag to determine if the menu should be displayed
      if (urlFeatureFlagEnabled) {
        return
      }

      if (!pauseOnFocusLost) {
        return
      }

      setPlaying(false)
    }

    // Auto pause when focus is lost
    const checkFocusAndVisibility = (): void => {
      const hasFocus = document.visibilityState === 'visible' &&
        (keyboardControls.isElementFocused(FocusableElement.ButtonCc) ||
         keyboardControls.isElementFocused(FocusableElement.ButtonPlayPause) ||
         keyboardControls.isElementFocused(FocusableElement.ButtonRewind))

      if (!hasFocus) {
        pauseVideoFocusLost()
      }
    }

    checkFocusAndVisibility()

    const handleWindowFocus = (): void => {
      checkFocusAndVisibility()
    }

    const handleWindowBlur = (): void => {
      pauseVideoFocusLost()
    }

    document.addEventListener('visibilitychange', checkFocusAndVisibility)
    window.addEventListener('focus', handleWindowFocus)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      document.removeEventListener('visibilitychange', checkFocusAndVisibility)
      window.removeEventListener('focus', handleWindowFocus)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [keyboardControls, pauseOnFocusLost, urlFeatureFlagEnabled])

  const setPlayerRef = useCallback((): void => {
    // this is a workaround for this open bug https://github.com/cookpete/react-player/issues/1268
    // this function should only fire when the player loads, however when the scrubber is moved,
    // this onLoad callback fires thus disabling the already enabled captions
    if (!userPreferences.preferenceA11yCc) {
      const internalPlayer = playerRef?.current?.getInternalPlayer()
      if (internalPlayer !== null && internalPlayer !== undefined) {
        // initially turn all subtitles off
        for (let i = 0; i < internalPlayer?.textTracks.length; i++) {
          internalPlayer.textTracks[i].mode = 'hidden'
        }
        if (!isVideoLoaded) {
          setIsVideoLoaded(true)
        }
      }
    }
  }, [userPreferences.preferenceA11yCc, isVideoLoaded])

  useEffect(() => {
    const player = playerRef.current?.getInternalPlayer()
    if (player?.textTracks !== null && player?.textTracks !== undefined && player.textTracks.length > 0) {
      player.textTracks[0].mode = userPreferences.preferenceA11yCc ? 'showing' : 'hidden'
    }
  }, [userPreferences.preferenceA11yCc])

  const handleMouseOut: MouseEventHandler<HTMLDivElement> = (_event): void => {
    if (instructionMode) {
      setHideControls(true)
    }
  }

  const handleMouseOver: MouseEventHandler<HTMLDivElement> = (_event): void => {
    if (instructionMode) {
      setHideControls(false)
    }
  }

  const handleCaptionClick = useCallback((keyPressed?: 'Enter' | ' '): void => {
    const newCaptionsState = keyPressed === ' ' ? !userPreferences.preferenceA11yCc : true
    userPreferences.setPreferenceA11yCc(newCaptionsState)
    window.dispatchEvent(new CustomEvent('captionsChanged', { detail: newCaptionsState }))
  }, [userPreferences])

  const handlePlayClick = useCallback((): void => {
    if (playerRefTimer.current !== null) {
      clearInterval(playerRefTimer.current)
      playerRefTimer.current = null
    }
    setPlaying(!playing)
  }, [playing])

  /**
   * Rewinds the video player.
   */
  const handleRewindClick = useCallback((keyPressed: 'Enter' | ' '): void => {
    const rewindInterval = 5 // seconds to rewind
    if (playerRefTimer.current === null) {
      setPlaying(false)
      const intervalId = playerRefTimer.current = window.setInterval(() => {
        const currentTime = playerRef?.current?.getCurrentTime() ?? 0
        if (currentTime >= rewindInterval) {
          playerRef?.current?.seekTo(currentTime - rewindInterval, 'seconds')
        } else {
          playerRef?.current?.seekTo(0)
          clearInterval(intervalId)
          playerRefTimer.current = null
        }
      }, 500)
    } else if (keyPressed === ' ') {
      clearInterval(playerRefTimer.current)
      playerRefTimer.current = null
      setPlaying(true)
    }
  }, [])

  return (
    <div className='w-full h-full'>
      <ReactPlayer
        data-testid={idVideoPlayer}
        ref={playerRef}
        className='object-contain'
        url={url}
        config={{
          file: {
            attributes: { crossOrigin: 'true' },
            tracks: [
              { kind: 'subtitles', src: subTitlesUrl ?? '', srcLang: 'en', default: true, label: '' }
            ]
          }
        }}
        playing={playing}
        muted={false}
        controls={urlFeatureFlagEnabled || urlFeatureFlagTrainEnabled}
        width='100%'
        height='100%'
        onPlay={handlePlay}
        onPause={handlePause}
        onProgress={handleProgress}
        onEnded={() => handleEnded(url)}
        onReady={setPlayerRef}
      />
      <div
        id={idControls}
        data-testid={idControls}
        role='menu'
        tabIndex={0}
        className={clsx('flex items-center custom-controls ', { 'items-center': !instructionMode, 'opacity-0': hideControls, 'w-full': instructionMode, 'mt-4': instructionMode, 'mt-8': !instructionMode })}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        onFocus={() => { }}
        onBlur={() => { }}
      >
        {secondaryLeftComponent ?? null}

        <div className={clsx('flex items-center justify-center absolute left-1/2 transform -translate-x-1/2', { 'w-[20vh]': instructionMode, 'bottom-[45px]': instructionMode, 'left-[45%]': instructionMode, 'translate-y-4': instructionMode })}>
          {controlLeftComponent ?? null}

          <div className='px-4'>
            {playerButtonsDisabled
              ? (
                <button
                  className='cursor-not-allowed'
                  type='button'
                >
                  <img src={rewindIcon} alt='rewind icon' />

                </button>
                )
              : (
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonRewind)}
                  highlightEffect={HighlightEffect.ButtonVideoPlayer}
                  data-testid={`highlight-${FocusableElement.ButtonRewind}`}
                >
                  <KeyboardControl>
                    <Button
                      id={FocusableElement.ButtonRewind}
                      data-testid={FocusableElement.ButtonRewind}
                      className='cursor-pointer block relative z-[1] outline-none bg-transparent'
                      onClick={() => handleRewindClick(' ')}
                      type='button'
                      aria-label='Rewind video'
                    >
                      <img
                        src={rewindFullIcon}
                        alt='rewind icon'
                      />
                    </Button>
                  </KeyboardControl>
                </ControlHighlight>
                )}
          </div>
          <div className='px-4'>
            {playerButtonsDisabled
              ? (
                <button
                  className='cursor-not-allowed'
                  type='button'
                  aria-label={playing ? 'Pause video' : 'Play video'}
                >
                  <img src={pauseIcon} alt='play/pause video icon' />
                </button>
                )
              : (
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonPlayPause)}
                  highlightEffect={HighlightEffect.ButtonVideoPlayer}
                  data-testid={`highlight-${FocusableElement.ButtonPlayPause}`}
                >
                  <KeyboardControl>
                    <Button
                      tabIndex={0}
                      id={FocusableElement.ButtonPlayPause}
                      data-testid={FocusableElement.ButtonPlayPause}
                      className='cursor-pointer block relative z-[1] outline-none bg-transparent'
                      type='button'
                      aria-label={playing ? 'Pause video' : 'Play video'}
                      onClick={handlePlayClick}
                    >
                      <img
                        src={playing ? pauseFullIcon : playIcon}
                        alt='play/pause video icon'
                      />
                    </Button>
                  </KeyboardControl>
                </ControlHighlight>
                )}
          </div>
          <div className={clsx('px-4', { hidden: instructionMode })}>
            {playerButtonsDisabled
              ? (
                <Button
                  id={FocusableElement.ButtonCc}
                  data-testid={FocusableElement.ButtonCc}
                  className='cursor-pointer block relative z-[1] outline-none bg-transparent'
                  type='button'
                  aria-label='Toggle captions'
                  onClick={() => handleCaptionClick(' ')}
                >
                  <img src={ccIcon} alt='cc icon' />
                </Button>
                )
              : (
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonCc)}
                  highlightEffect={HighlightEffect.ButtonVideoPlayer}
                  data-testid={`highlight-${FocusableElement.ButtonCc}`}
                >
                  <KeyboardControl>
                    <Button
                      id={FocusableElement.ButtonCc}
                      data-testid={FocusableElement.ButtonCc}
                      className='cursor-pointer block relative z-[1] outline-none bg-transparent'
                      aria-label='Toggle captions'
                      onClick={() => handleCaptionClick(' ')}
                      type='button'
                    >
                      <img
                        src={ccFullIcon}
                        alt='cc icon'
                      />
                    </Button>
                  </KeyboardControl>
                </ControlHighlight>
                )}
          </div>

          {controlRightComponent ?? null}
        </div>
      </div>
    </div>
  )
}

export default memo(VideoPlayer)
